import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, MoonStar } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

/* ============================================================
   CONFIGURATION DE L'OFFRE — modifie simplement ce bloc.
   Le bandeau ne s'affiche QU'ENTRE start et end.
   Le code promo doit exister dans Shopify → Réductions.
   Aperçu à tout moment : ajoute ?promo=test à l'URL du site.
   ============================================================ */
const PROMO = {
  enabled: true,
  start: "2027-02-01T00:00:00+01:00", // début d'affichage du bandeau
  end: "2027-03-09T23:59:59+01:00", // fin (le compte à rebours pointe ici)
  eyebrow: "Offre Ramadan",
  title: "Pack Gold à −20 %",
  code: "RAMADAN20",
  cta: "J'en profite",
  target: "#packs",
};

function useCountdown(end) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(end).getTime() - now);
  return {
    j: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
    over: diff <= 0,
  };
}

export default function PromoBanner() {
  const isTest = useMemo(
    () => new URLSearchParams(window.location.search).get("promo") === "test",
    []
  );
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("promo-dismissed") === "1"
  );

  // Fenêtre calculée UNE SEULE FOIS (sinon le compte à rebours se régénère à chaque tick)
  const [window_] = useState(() => ({
    start: isTest ? Date.now() - 1000 : new Date(PROMO.start).getTime(),
    end: isTest ? Date.now() + 72 * 3600000 : PROMO.end,
  }));
  const { j, h, m, s, over } = useCountdown(window_.end);

  const visible = PROMO.enabled && !dismissed && !over && Date.now() >= window_.start;

  // Décale la nav fixe sous le bandeau (sinon elle recouvre les boutons du bandeau)
  const bannerRef = useRef(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty("--promo-h", "0px");
      return;
    }
    const apply = () =>
      root.style.setProperty("--promo-h", `${bannerRef.current?.offsetHeight || 0}px`);
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      root.style.setProperty("--promo-h", "0px");
    };
  }, [visible]);

  if (!visible) return null;

  const go = () => scrollToSection(PROMO.target);

  const cells = [
    { v: j, label: "jours" },
    { v: h, label: "heures" },
    { v: m, label: "min" },
    { v: s, label: "sec" },
  ];

  return (
    <motion.div
      ref={bannerRef}
      className="promo-banner"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="promo-banner"
    >
      <MoonStar size={17} className="promo-moon" />
      <span className="promo-text">
        <b>{PROMO.eyebrow}</b> — {PROMO.title} avec le code <code>{PROMO.code}</code>
      </span>
      <div className="promo-count" data-testid="promo-countdown">
        {cells.map((c) => (
          <span className="promo-cell" key={c.label}>
            <b>{String(c.v).padStart(2, "0")}</b>
            <i>{c.label}</i>
          </span>
        ))}
      </div>
      <button className="promo-cta" onClick={go} data-testid="promo-cta">
        {PROMO.cta}
      </button>
      <button
        className="promo-close"
        aria-label="Fermer le bandeau promo"
        data-testid="promo-close"
        onClick={() => {
          sessionStorage.setItem("promo-dismissed", "1");
          setDismissed(true);
        }}
      >
        <X size={15} />
      </button>
    </motion.div>
  );
}
